import apiClient from './config';
import { Client, SepaTransaction, ReportingData, ClienteSepa } from "../../types/index";

// Interfaz para la respuesta de transacciones SEPA con estado
interface SepaTransactionsResponse {
    transactions: SepaTransaction[];
    isMonthClosed: boolean;
}

// Interfaz para cerrar mes SEPA
interface CloseSepaMonthParams {
    mes: number;
    anio: number;
    sepaClients: Array<{
        cliente_id: string;
        suscripcion_id: string;
        iban: string;
        importe: number;
        observaciones: string;
    }>;
}

// Interfaz para la respuesta de cerrar mes
interface CloseSepaMonthResponse {
    success: boolean;
    message: string;
    transaction: SepaTransaction;
}

// Interfaz para toggle mes inactivo
interface ToggleInactiveMonthParams {
    suscripcionId: string;
    mes: number;
}

// Interfaz para eliminar cliente/suscripción SEPA
interface DeleteSepaClientParams {
    clientId: string;
    subscriptionId?: string;
}

// Interfaz para la respuesta de eliminar cliente
interface DeleteSepaClientResponse {
    success: boolean;
    message: string;
    updatedPayer?: {
        subscription_id: string;
        client_id: string;
    };
}

// =====================================================
// SERVICIOS API
// =====================================================

/**
 * Obtener clientes SEPA activos con filtrado de domiciliaciones
 */
export const fetchSepaClients = async (): Promise<Client[]> => {
    const response = await apiClient.get('/client-subscriptions?activo=true');
    const data: Client[] = response.data;
    
    // Aplicar el mismo filtrado que en el componente actual
    const filteredData = data.filter(client => 
        // Incluir clientes activos
        client.activo &&
        // Incluir clientes sin suscripciones
        client.suscripciones.length === 0 ||
        // O clientes con al menos una suscripción de nombre "pilates-domiciliacion"
        client.suscripciones.some(suscripcion => suscripcion.nombreSuscripcion === "pilates-domiciliacion")
    ).map(client => ({
        ...client,
        // Si el cliente tiene suscripciones, filtrarlas para incluir solo las de tipo "Domiciliaciones"
        // Si no tiene suscripciones, mantener el array vacío
        suscripciones: client.suscripciones.length > 0 
            ? client.suscripciones.filter(suscripcion => suscripcion.nombreSuscripcion === "pilates-domiciliacion")
            : []
    }));
    
    return filteredData;
};

/**
 * Obtener transacciones SEPA del mes siguiente
 */
export const fetchSepaTransactions = async (): Promise<SepaTransactionsResponse> => {
    const response = await apiClient.get('/sepa-transactions');
    const data: SepaTransaction[] = response.data;
    
    // Calcular próximo mes y año
    const now = new Date();
    const nextMonth = now.getMonth() + 2; // getMonth() es 0-based, y queremos el siguiente
    const currentYear = now.getFullYear();
    const adjustedMonth = nextMonth > 12 ? nextMonth - 12 : nextMonth;
    const adjustedYear = nextMonth > 12 ? currentYear + 1 : currentYear;
    
    if (data.length === 0) {
        return { transactions: [], isMonthClosed: false };
    }
    
    const filteredData = data.filter(transaction => 
        transaction.mes === adjustedMonth && transaction.anio === adjustedYear
    );
    
    const isMonthClosed = filteredData.length > 0 && filteredData[0].estado !== "Abierto";
    
    return { transactions: filteredData, isMonthClosed };
};

/**
 * Toggle mes inactivo para una suscripción
 */
export const toggleInactiveMonth = async ({ suscripcionId, mes }: ToggleInactiveMonthParams): Promise<void> => {
    await apiClient.patch(`/subscriptions/${suscripcionId}/inactive-months`, { mes });
};

/**
 * Cerrar mes SEPA
 */
export const closeSepaMonth = async (params: CloseSepaMonthParams): Promise<CloseSepaMonthResponse> => {
    const response = await apiClient.post('/sepa-workflow/close', params);
    return response.data;
};

/**
 * Eliminar cliente/suscripción SEPA
 */
export const deleteSepaClient = async ({ clientId, subscriptionId }: DeleteSepaClientParams): Promise<DeleteSepaClientResponse> => {
    let endpoint = `/client-subscriptions/${clientId}`;
    
    if (subscriptionId) {
        endpoint += `/${subscriptionId}`;
    }
    
    const response = await apiClient.delete(endpoint);
    return response.data;
};

/**
 * Obtener datos de reporte (merge de pagos y transacciones SEPA)
 */
export const fetchReportingData = async (): Promise<ReportingData[]> => {
    const response = await apiClient.get('/sepa-workflow/merge-payments-and-transactions');
    return response.data;
};

/**
 * Obtener clientes SEPA de una transacción específica
 */
export const fetchSepaClientsByTransactionId = async (transactionId: string): Promise<ClienteSepa[]> => {
    const response = await apiClient.get(`/sepa-clients/${transactionId}`);
    return response.data;
};

/**
 * Crear o buscar una transacción SEPA
 */
export const createOrFetchSepaTransaction = async (mes: number, anio: number, createdBy: string): Promise<{ message: string }> => {
    const response = await apiClient.post('/sepa-workflow/sepa-transaction', { mes, anio, createdBy });
    return response.data;
};